import { useMemo } from "react";
import { bool, string, oneOf, func, node, number, oneOfType } from "prop-types";
import { observer } from "mobx-react";
import { Totals } from "@repo/utils";
import { getTotalsTreePath } from "@repo/structure";
import {
  usePresetFieldNames,
  useEstimateEditorStore,
  useEstimatePresetStore,
  useEstimateEditorSettings,
  VisibilityButton,
  ElementChatPopup,
} from "@tools";
import { calculateTextWidth } from "@utils";
import { capitalize, Divider, Grid } from "@material-ui/core";
import { TotalValue, TotalValueEditable } from "./index";
import { DragIndicator } from "@material-ui/icons";
import classnames from "classnames";
import useStyle from "../Totals.style";

export const TotalContainer = observer(({
  children,
  totalName,
  totalAltName,
  showDivider,
  editable,
  onValueChange,
  visibilityHidden,
  setVisibility,
  className,
  compact,
  textSmaller,
  valueClassName,
  style,
  ...valueProps
}) => {
  const classes = useStyle();
  const presetStore = useEstimatePresetStore();
  const { visibilityMode, profitabilityMode } = useEstimateEditorStore();
  
  const {
    allowEdition,
    showChatThread,
    isProposal,
  } = useEstimateEditorSettings();
  
  const { getFieldName } = usePresetFieldNames(isProposal);
  
  const commentPath = useMemo(() => (
    Object.values(Totals).includes(totalName) && getTotalsTreePath(totalName)
  ), [totalName]);
  
  const presetTotalName = useMemo(() => (
    capitalize(totalAltName || totalName)
  ), [ totalName, totalAltName ]);
  
  const title = getFieldName(`pt-total${presetTotalName}`);
  
  const longestWordWidth = useMemo(() => {
    if(!compact)
      return;
    
    const word = title.split(/\s/).find(w => w.length >= 8);
    return word
      ? calculateTextWidth(word, { fontSize: "11px", fontWeight: 400, textTransform: "uppercase" }, 4)
      : "9ch";
  }, [ compact, title, presetStore?.selectedPresetFont ]);
  
  return (
    <Grid
      item
      container
      className={classnames(
        "hoverRow w-max vCon total-container",
        compact ? "px-3" : "px-4",
        allowEdition && !compact && "cursor-pointer",
        className
      )}
    >
      <Grid
        item container
        className="relative"
        direction={compact ? "row" : "column"}
        alignItems={compact ? "center" :"flex-end"}
        justifyContent="center"
      >
        {
          allowEdition && !compact && !profitabilityMode &&
          <DragIndicator
            className={classnames(
              "dragHandle hoverIcon",
              classes.handle,
            )}
          />
        }
        {
          compact &&
          <span
            className={classnames(
              "w-max mr-2 h-max",
              classes.totalTitleWrapper,
              `preset-total${presetTotalName}Title pt-total${presetTotalName}`,
            )}
            style={{ maxWidth: longestWordWidth }}
          >
            { title }
          </span>
        }
        {
          !compact &&
          <Grid
            item container
            alignItems="center"
            justifyContent="flex-end"
            className={classnames(
              "w-max h-12",
              `preset-total${presetTotalName}Title pt-total${presetTotalName}`,
              classes.totalTitleWrapper
            )}
          >
            {
              showChatThread && commentPath && !profitabilityMode &&
              <ElementChatPopup
                rowThread
                path={commentPath}
                className="text-sm p-0-5 mx-2 -mt-0-5"
                taskName={ title }
                containerClass="total-container"
              />
            }
            {
              visibilityMode &&
              <VisibilityButton hidden={visibilityHidden} onChange={setVisibility} size="tiny" />
            }
            { title }
          </Grid>
        }
        <Grid
          item
          container
          alignItems="center"
          className={classnames(
            `preset-total${presetTotalName}`,
            "preset-total w-max font-bold",
            textSmaller || compact ? "text-2xl" : "text-4xl",
            textSmaller && !compact && "mt-2 text-secondary",
            valueClassName
          )}
          style={style}
        >
          {
            onValueChange && editable && allowEdition && !compact
              ? <TotalValueEditable
                // name={totalName}
                onValueChange={onValueChange}
                {...valueProps}
              >
                {children}
              </TotalValueEditable>
              : <TotalValue
                // name={totalName}
                {...valueProps}
              />
          }
        </Grid>
        {
          !compact && showDivider &&
          <Divider orientation="vertical" className={classes.divider} />
        }
      </Grid>
    </Grid>
  )
});

TotalContainer.propTypes = {
  min: oneOfType([number, string]).isRequired,
  max: oneOfType([number, string]),
  children: node, //additional items to show after value & symbols when editable
  onValueChange: func,
  symbolStart: oneOfType([string, node]),
  symbolEnd: oneOfType([string, node]),
  totalName: oneOf(["hours", "time", "cost", "companyCost", "revenue", "profit", "profitability"]).isRequired,
  showMaxValue: bool,
  showDivider: bool,
  editable: bool,
  showDecimals: bool,
  applySuffixSpacing: bool,
  textSmaller: bool,
  visibilityHidden: bool,
  compact: bool,
  setVisibility: func,
  className: string,
  minTooltipMessage: node,
  maxTooltipMessage: node,
}